import { useCallback, useEffect, useReducer, useRef } from "react";

type TypeMultiSelectProps<T extends string> = {
  initialValue?: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: Array<string | T>) => any;
};

const valueReducer = (
  state: Set<string>,
  action: { type: string; value?: string }
): Set<string> => {
  const newState = new Set(state);
  switch (action.type) {
    case "reset": {
      return new Set();
    }
    case "toggle_item":
    default: {
      if (action.value) {
        if (newState.has(action.value)) {
          newState.delete(action.value);
        } else {
          newState.add(action.value);
        }
      }
      return newState;
    }
  }
};

export const useMultiselect = <T extends string>(
  {
    initialValue = [],
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onChange: userOnChange = () => {},
  }: TypeMultiSelectProps<T> = {
    initialValue: [],
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onChange: () => {},
  }
) => {
  const [value, dispatch] = useReducer(valueReducer, new Set(initialValue));

  const getArrayValue = (value: Set<string | T>) =>
    Array.from<string | T>(value);

  const onChange = useCallback(
    (newValue: string) => {
      dispatch({ type: "toggle_item", value: newValue });
    },
    [dispatch]
  );

  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    userOnChange(getArrayValue(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOnChange, value]);

  const onClear = useCallback(() => {
    dispatch({ type: "reset" });
  }, [dispatch]);

  return { value: getArrayValue(value), onChange, onClear };
};
